// Ocean Insight Pro Mobile - Error Boundary Component
// Catches JavaScript errors anywhere in the child component tree

import React, { Component, ReactNode } from 'react';
import { View, Text, StyleSheet } from 'react-native';
import { Button, Card, Title, Paragraph } from 'react-native-paper';
import ProductionErrorFallback from './ProductionErrorFallback';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: string;
}

export default class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    // Update state so the next render will show the fallback UI
    console.error('[ErrorBoundary] Error caught:', error.message);
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Log error to console and crash reporting service
    console.error('[ErrorBoundary] Full error details:', {
      error: error.toString(),
      message: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
    });

    this.setState({
      error,
      errorInfo: errorInfo.componentStack || undefined,
    });

    // In production, you would send this to a crash reporting service
    // Example: Sentry.captureException(error);
  }

  handleReset = () => {
    console.log('[ErrorBoundary] Resetting error state');
    this.setState({ hasError: false, error: undefined, errorInfo: undefined });
  };

  render() {
    if (this.state.hasError && this.state.error) {
      // Use production error fallback in production
      if (!__DEV__) {
        return (
          <ProductionErrorFallback 
            error={this.state.error} 
            resetError={this.handleReset}
          />
        );
      }

      // Development error display
      return (
        <View style={styles.container}>
          <Card style={styles.errorCard}>
            <Card.Content>
              <Title style={styles.errorTitle}>Oops! Something went wrong</Title>
              <Paragraph style={styles.errorMessage}>
                The app encountered an unexpected error. We apologize for the inconvenience.
              </Paragraph>

              <Button mode="contained" onPress={this.handleReset} style={styles.retryButton}>
                Try Again
              </Button>

              {__DEV__ && (
                <View style={styles.debugInfo}>
                  <Text style={styles.debugTitle}>Debug Information:</Text>
                  <Text style={styles.debugText}>{this.state.error?.toString()}</Text>
                  {this.state.errorInfo && (
                    <Text style={styles.debugText}>{this.state.errorInfo}</Text>
                  )}
                </View>
              )}
            </Card.Content>
          </Card>
        </View>
      );
    }

    return this.props.children;
  }
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
    backgroundColor: '#f5f5f5',
  },
  errorCard: {
    width: '100%',
    maxWidth: 400,
  },
  errorTitle: {
    textAlign: 'center',
    marginBottom: 16,
    color: '#d32f2f',
  },
  errorMessage: {
    textAlign: 'center',
    marginBottom: 24,
    lineHeight: 22,
  },
  retryButton: {
    marginTop: 16,
  },
  debugInfo: {
    marginTop: 24,
    padding: 16,
    backgroundColor: '#f0f0f0',
    borderRadius: 8,
  },
  debugTitle: {
    fontWeight: 'bold',
    marginBottom: 8,
    color: '#666',
  },
  debugText: {
    fontSize: 12,
    color: '#666',
    fontFamily: 'monospace',
  },
});
